import { defineTable } from "convex/server";
import type { Infer } from "convex/values";
import { v } from "convex/values";

import type { Doc } from "../_generated/dataModel";

/** Canonical supply quantity units (UI labels + mutation/schema args share this list). */
export const PROJECT_SUPPLY_QUANTITY_UNITS = ["kg", "l", "m3", "unit", "pack", "ton"] as const;
export type ProjectSupplyQuantityUnit = (typeof PROJECT_SUPPLY_QUANTITY_UNITS)[number];

export const projectSupplyQuantityUnit = v.union(
  v.literal("kg"),
  v.literal("l"),
  v.literal("m3"),
  v.literal("unit"),
  v.literal("pack"),
  v.literal("ton"),
);

export const projectAcquisitionType = v.union(v.literal("immediate"), v.literal("planned"));
export type ProjectAcquisitionType = Infer<typeof projectAcquisitionType>;

export const projectBudgetMode = v.union(v.literal("detailed"), v.literal("simplified"));
export type ProjectBudgetMode = Infer<typeof projectBudgetMode>;

export const projectSuppliesTable = defineTable({
  projectId: v.id("projects"),
  supplyGroupId: v.id("base_project_supply_groups"),
  supplyId: v.id("base_project_supplies"),
  budgetMode: v.optional(projectBudgetMode),
  quantity: v.optional(v.number()),
  quantityUnitOfMeasurement: v.optional(projectSupplyQuantityUnit),
  unitPrice: v.optional(v.number()), // BRL per quantity unit (e.g. per kg) for per-ha budgeting
  totalValue: v.number(), // BRL (aligned with web forms)
  ownResourcesValue: v.number(), // BRL
  expectedAcquisitionType: projectAcquisitionType,
  useStartDate: v.number(), // timestamp
  useEndDate: v.number(), // timestamp
  organizationId: v.string(),

  // Audit fields
  createdBy: v.string(),
  updatedBy: v.string(),
  createdAt: v.number(),
  updatedAt: v.number(),
})
  .index("by_project", ["projectId"])
  .index("by_organization", ["organizationId"])
  .index("by_supply_group", ["supplyGroupId"])
  .index("by_acquisition_type", ["expectedAcquisitionType"]);

export type ProjectSuppliesTable = Doc<"project_supplies">;
